import type { RichBlock } from '@/src/types/RichBlock';

export type RichBlockTemplate = {
  id: string;
  label: string;
  description: string;
  block: RichBlock;
};

export const richBlockTemplates: RichBlockTemplate[] = [
  {
    id: 'comparaison-internaliser-taskforce',
    label: 'Internaliser vs taskforce senior',
    description: 'Tableau comparatif pour aider le décideur à choisir son mode d’exécution.',
    block: {
      type: 'comparison',
      title: 'Internaliser un projet IA ou faire appel à une taskforce senior ?',
      headers: ['Critère', 'Internaliser', 'Taskforce senior'],
      rows: [
        ['Time-to-value', '6 à 18 mois (recrutement + montée en compétence)', '4 à 6 semaines'],
        ['Expertise', 'Variable selon les profils recrutés', 'Confirmée et immédiate'],
        ['Risque projet', 'Porté en interne', 'Partagé, avec livrables intermédiaires'],
        ['Transfert', 'À construire', 'Documenté et formant dès le départ'],
        ['Coût direct', 'Salaires + outils + management', 'Forfait au résultat'],
      ],
      caption: 'Le choix dépend de la criticité du projet, du délai et de la maturité IA interne.',
    },
  },
  {
    id: 'checklist-mvp-review',
    label: '5 signes que le MVP a besoin d’une revue',
    description: 'Checklist actionnable pour identifier les MVPs en danger.',
    block: {
      type: 'checklist',
      title: '5 signes que votre MVP IA a besoin d’une revue senior',
      items: [
        {
          text: 'La démo fonctionne, mais personne ne sait comment le mettre en production.',
          checked: false,
        },
        {
          text: 'Le modèle marche sur vos données de test, pas sur les données réelles.',
          checked: false,
        },
        {
          text: 'Personne ne peut expliquer pourquoi le modèle a donné telle réponse.',
          checked: false,
        },
        {
          text: 'Les tests, la sécurité et l’observabilité ne sont pas dans le périmètre.',
          checked: false,
        },
        {
          text: 'L’équipe interne n’a pas le temps d’apprendre à maintenir le livrable.',
          checked: false,
        },
      ],
    },
  },
  {
    id: 'graphique-time-to-value',
    label: 'Temps de valeur : prototypage vs industrialisation',
    description:
      'Graphique en barres pour illustrer le décalage entre expérimentation et valeur réelle.',
    block: {
      type: 'chart',
      chartType: 'bar',
      title: 'Temps moyen pour créer de la valeur IA',
      labels: ['Prototype rapide', 'MVP validé', 'MVP production', 'Industrialisation'],
      values: [2, 8, 24, 52],
      caption: 'En semaines. Le goulot d’étranglement se déplace de la génération à l’intégration.',
    },
  },
  {
    id: 'quiz-readiness',
    label: 'Quiz : évaluez votre readiness IA',
    description: 'Questionnaire interactif pour engager le lecteur et qualifier son niveau.',
    block: {
      type: 'quiz',
      title: 'Votre readiness IA en 3 questions',
      questions: [
        {
          question: 'Votre MVP IA actuel est-il exécuté dans un environnement de production ?',
          choices: [
            'Oui, avec monitoring et tests automatisés',
            'Oui, mais sans surveillance systématique',
            'Non, il reste en local ou en démo',
          ],
          correctIndex: 0,
          explanation:
            'Un modèle en production sans monitoring et tests expose l’entreprise à des régressions difficiles à détecter.',
        },
        {
          question: 'Qui peut aujourd’hui maintenir et faire évoluer le modèle en interne ?',
          choices: [
            'Une équipe dédiée avec les compétences adéquates',
            'Une ou deux personnes seulement',
            'Personne : cela dépend entièrement du prestataire',
          ],
          correctIndex: 0,
          explanation:
            'La dépendance à un individu ou à un prestataire fragilise la durabilité du projet.',
        },
        {
          question: 'Le ROI de l’IA est-il mesuré sur des indicateurs métier ?',
          choices: [
            'Oui : gains de temps, taux d’erreur, satisfaction client…',
            'Partiellement : on mesure surtout l’usage',
            'Non : on se base sur des estimations ou des démos',
          ],
          correctIndex: 0,
          explanation:
            'Mesurer l’usage plutôt que la valeur métier conduit à investir dans des projets qui ne changent pas les résultats.',
        },
      ],
      passingScore: 70,
    },
  },
  {
    id: 'faq-objections-esn',
    label: 'FAQ : objections ESN',
    description: 'Accordéon avec les objections fréquentes des grandes ESN et grands comptes.',
    block: {
      type: 'expandable',
      title: 'Une taskforce externe ne connaît pas notre contexte',
      content:
        'C’est justement le rôle des premières semaines : cartographier le contexte métier, les contraintes techniques et les parties prenantes. Un bon partenaire documente ce contexte et le rend actionnable pour l’équipe interne.',
    },
  },
  {
    id: 'faq-securite',
    label: 'FAQ : sécurité et données',
    description: 'Accordéon sur la sécurité des données.',
    block: {
      type: 'expandable',
      title: 'Nos données sont sensibles : comment garantir la sécurité ?',
      content:
        'Les données peuvent rester dans votre infrastructure ou dans un environnement isolé. L’architecture, les droits d’accès, l’anonymisation et l’audit trail sont définis avant tout entraînement ou déploiement.',
    },
  },
  {
    id: 'certificat-lecture',
    label: 'Certificat de complétion',
    description: 'Bloc certificat téléchargeable pour fidéliser le lecteur.',
    block: {
      type: 'certificate',
      title: 'Analyse Averiane — IA en entreprise',
      description:
        'Certificat de complétion délivré après avoir lu l’analyse sur la mise en production de l’IA en entreprise.',
      criteria: 'Avoir lu l’article en intégralité.',
    },
  },
  {
    id: 'image-cta',
    label: 'Visuel CTA 6 semaines',
    description: 'Placeholder pour un visuel récapitulatif du processus 6 semaines.',
    block: {
      type: 'imagePlaceholder',
      prompt:
        'Minimalist business infographic showing a 6-week AI MVP timeline with milestones: scope, build, test, deploy, transfer. Navy blue and warm accent colors, professional B2B style, no text.',
      caption: 'Schéma du processus MVP en 6 semaines.',
    },
  },
  {
    id: 'template-confluence-action',
    label: 'Template Confluence — plan d’action',
    description: 'Checklist téléchargeable à importer dans Confluence pour passer à l’action.',
    block: {
      type: 'confluenceTemplate',
      title: 'Plan d’action — Industrialiser une preuve de concept IA',
      description:
        'Du MVP à la production : les points à vérifier, les décisions à prendre et les livrables à exiger. À copier dans Confluence ou Notion.',
      fileName: 'plan-action-mvp-ia',
      items: [
        {
          text: 'Identifier les 3 indicateurs métier qui prouveront la valeur du MVP.',
          checked: false,
        },
        {
          text: 'Cartographier les données réelles utilisées en production vs. données de démo.',
          checked: false,
        },
        {
          text: 'Définir qui peut maintenir le modèle en interne et quelles compétences manquent.',
          checked: false,
        },
        {
          text: 'Valider la sécurité, les droits d’accès et la traçabilité des prédictions.',
          checked: false,
        },
        {
          text: 'Prévoir les tests de non-régression et un monitoring de la performance.',
          checked: false,
        },
        {
          text: 'Estimer le coût total de possession sur 12 mois (infrastructure + maintenance).',
          checked: false,
        },
        {
          text: 'Planifier une revue technique senior avant tout passage en production.',
          checked: false,
        },
      ],
    },
  },
];

/**
 * Blocs riches pré-remplis par défaut pour un article de prospection B2B.
 * Ils servent de base actionnable : l’éditeur peut les compléter, les remplacer
 * ou les réorganiser avant publication.
 */
export function getDefaultProspectionBlocks(): RichBlock[] {
  return richBlockTemplates
    .filter((t) =>
      [
        'checklist-mvp-review',
        'comparaison-internaliser-taskforce',
        'quiz-readiness',
        'certificat-lecture',
        'faq-objections-esn',
        'graphique-time-to-value',
      ].includes(t.id)
    )
    .map((t) => ({ ...t.block }));
}
